import { useContext, useState } from 'react';
import { T } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/context/auth.context';
import { UserAutocomplete } from '@/components/users/UserAutocomplete';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Picker a Gently admin uses to start view-only imitation of another user in
 * the current organization. Cross-org targets are rejected server-side — switch
 * organization first, then imitate.
 *
 * Rendered outside the sidebar's `DropdownMenu` (the menu closes on select, so
 * a dialog mounted inside it would unmount with it) and driven by state owned
 * by `NavUser`.
 */
export function ImitateUserDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mount the form only while open so the selection resets each time. */}
        {open && <ImitateUserForm onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function ImitateUserForm({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const { currentUser, startImitation } = useContext(AuthContext);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );
  const [isStarting, setIsStarting] = useState(false);

  const handleStart = async () => {
    if (!selectedUserId) return;
    setIsStarting(true);
    try {
      // Reloads the page on success, so this only resolves when starting
      // failed — in which case the dialog has to become usable again.
      await startImitation(selectedUserId);
    } catch {
      // startImitation never rejects (it logs internally); the clause exists so
      // the React Compiler doesn't bail on a catch-less try (react-hooks todo).
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="title of the dialog where a Gently admin picks a user to view the app as">
            Imitate a user
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="subtitle of the imitate user dialog">
            See the app exactly as someone in this organization sees it. The
            session is strictly view-only — nothing can be changed while
            imitating.
          </T>
        </DialogDescription>
      </DialogHeader>

      {/* min-w-0: the dialog is a grid; without it the picker's nowrap
          "Name (email)" label widens the track past the dialog edge. */}
      <div className="min-w-0 py-2">
        <UserAutocomplete
          userId={selectedUserId}
          onSelect={setSelectedUserId}
          disabled={isStarting}
          // Imitating yourself is rejected server-side; keep it off the list.
          excludeUserIds={currentUser ? [currentUser.id] : undefined}
        />
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isStarting}
        >
          <T context="cancel button in the imitate user dialog">Cancel</T>
        </Button>
        <Button
          onClick={() => void handleStart()}
          disabled={!selectedUserId || isStarting}
        >
          {isStarting ? (
            <T context="submit button label while imitation is being started">
              Starting…
            </T>
          ) : (
            <T context="submit button that begins imitating the selected user">
              Start imitation
            </T>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
